import { compareEditedRowObjects, removeNullUndefined } from '../../shared/utils';
import { MakeOrBuyInputType, makeOrBuyRules } from './businessLogic';

function isSpeedlineDecisionYes(value) {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized !== '0' && normalized !== 'no' && normalized !== 'false';
  }
  return value !== 0 && value !== false;
}

function buildDecisionTreeInput({ rowData, editedRowOriginal, poKubuMapping, sheet1Htz }) {
  const {
    bestellnummer: bestellNummer,
    debitor_name: debitorName,
    design_owner_id: designOwnerId,
    htz_type_id: htzTypeId,
    npi_id: id,
    khk_pkey: khkPkey,
    lumpsum_due: lumpSumDue,
    npi_mob: makeOrBuy,
    novelty_id: noveltyId,
    npi_accept_po: npiAcceptPO,
    npi_as_kenner: npiAsKenner,
    npi_comment: npiComment,
    nrp_due: nrpDue,
    pag_deliverydate_due: pagDeliveryDateDue,
    pag_followup_date: pagFollowUpDate,
    pag_ispartof: pagIsPartOf,
    po_kubu_id: poKubuId,
    pre_htz: preHtz,
    pre_htz_manufactured_by: preHtzManufacturedBy,
    project_id: projectId,
    rp_due: rpDue,
  } = rowData;
  const poKubu = poKubuMapping.find((element) => element.id === poKubuId)?.po_kubu || null;
  const decisionTreeInput: MakeOrBuyInputType = {
    bestellNummer,
    debitorName,
    designOwnerId,
    htzTypeId,
    khkPkey,
    lumpSumDue,
    makeOrBuy,
    makeOrBuyModified: editedRowOriginal[id] && editedRowOriginal[id].npi_mob !== makeOrBuy,
    noveltyId,
    npiAcceptPO,
    npiAsKenner,
    npiComment,
    nrpDue,
    pagDeliveryDateDue,
    pagFollowUpDate,
    pagIsPartOf,
    poKubu,
    preHtz,
    preHtzManufacturedBy,
    projectId,
    rpDue,
    sheet1Htz,
  };
  return decisionTreeInput;
}
export function calcBusinessDecisions({
  editedRowOriginal,
  poKubuMapping,
  sheet1Htzs,
  upsertedRecords,
}) {
  const decisions = {};
  upsertedRecords.forEach((row) => {
    const { npi_htz: npiHtz } = row;
    const decisionTreeInput = buildDecisionTreeInput({
      rowData: row,
      editedRowOriginal,
      poKubuMapping,
      sheet1Htz: sheet1Htzs[npiHtz],
    });
    const decisionTreeResult = makeOrBuyRules(decisionTreeInput) ?? { save: true };
    decisions[npiHtz] = decisionTreeResult;
  });
  return decisions;
}

export function calcNpiCommit({ editedRowOriginal, upsertedRecords, userId }) {
  const npiInsertObjects = [];
  const npiUpdateObjects = [];
  const now = new Date().toISOString().substring(0, 19);
  upsertedRecords.forEach((record) => {
    const { npi_id: npiId, npi_mob: makeOrBuy, npi_mob_lastmodified: mobModifiedDate } = record.npi;
    if (npiId) {
      // npi table update
      const where = {};
      where['npi_id'] = {
        _eq: npiId,
      };
      const mobModified = editedRowOriginal[npiId].npi_mob !== makeOrBuy;
      const setData: Record<string, any> = {
        ...compareEditedRowObjects(record.npi, editedRowOriginal, 'npi_id'),
        updated_by: userId,
        update_date: now,
        npi_mob_lastmodified: mobModified ? now : mobModifiedDate,
      };

      if (!isSpeedlineDecisionYes(record.npi.speedline_dec)) {
        setData.speedline_plant = null;
        setData.speedline_qty = null;
        setData.speedline_due = null;
      }

      npiUpdateObjects.push({
        where,
        _set: setData,
      });
    } else {
      // npi table insert
      const setData: Record<string, any> = {
        ...removeNullUndefined(record.npi),
        created_by: userId,
        updated_by: userId,
        npi_mob_lastmodified: makeOrBuy ? now : null,
      };

      if (!isSpeedlineDecisionYes(record.npi.speedline_dec)) {
        setData.speedline_plant = null;
        setData.speedline_qty = null;
        setData.speedline_due = null;
      }

      npiInsertObjects.push(setData);
    }
  });

  return { npiInsertObjects, npiUpdateObjects };
}

export function calcSaphCommit({ decisions, saphIds, upsertedRecords, userId }) {
  const saphInsertObjects = [];
  const saphUpdateObjects = [];
  let allTableSuccess = true;

  upsertedRecords.forEach((record) => {
    const { bestellnummer: bestellNummer, npi_htz: npiHtz } = record.rowData;
    const decisionTreeResult = decisions[npiHtz];
    if (bestellNummer) {
      const saphSetFromDecisionTree = {};
      if (decisionTreeResult?.data) {
        const saphData = decisionTreeResult.data.find((item) => item.table === 'e_saph');
        const saphDTData = saphData ? saphData.fields : [];
        saphDTData.forEach((value, key) => (saphSetFromDecisionTree[key] = value));
      }
      const eSaphId = saphIds[npiHtz];
      if (eSaphId) {
        const where = {};
        where['id'] = { _eq: eSaphId };
        const saphUpdate = { ...record.e_saph, ...saphSetFromDecisionTree };
        saphUpdate['updated_by'] = userId;
        saphUpdate['update_date'] = new Date().toISOString().substring(0, 19);
        const saphSet = removeNullUndefined({ ...saphUpdate });
        saphUpdateObjects.push({
          where,
          _set: saphSet,
        });
      } else {
        const eSaphSet = removeNullUndefined({
          ...record.e_saph,
          ...saphSetFromDecisionTree,
          ['htz']: npiHtz,
          created_by: userId,
          updated_by: userId,
          update_date: new Date().toISOString().substring(0, 19),
        });
        eSaphSet.created_by = userId;
        saphInsertObjects.push(eSaphSet);
      }
    } else {
      allTableSuccess = false;
    }
  });

  return { allTableSuccess, saphInsertObjects, saphUpdateObjects };
}

function getSpeedlineValue(speedlineDecision, statusId) {
  if (speedlineDecision !== null && speedlineDecision !== undefined && speedlineDecision !== '') {
    return isSpeedlineDecisionYes(speedlineDecision) ? 1 : 0;
  }

  if (!statusId || statusId === 1) {
    return 0; // no
  }
  if (statusId === 2 || statusId === 3 || statusId === 4) {
    return 1; // yes
  }
  return null;
}

export function calcSheet1Commit({ decisions, upsertedRecords, sheet1Htzs, userId }) {
  const sheet1UpdateObjects = [];
  upsertedRecords.forEach((record) => {
    const { npi_htz: npiHtz } = record.rowData;
    const decisionTreeResult = decisions[npiHtz];
    const sheet1Htz = sheet1Htzs[npiHtz];
    if (sheet1Htz) {
      const sheet1SetFromDecisionTree = {};
      if (decisionTreeResult?.data) {
        const saphData = decisionTreeResult.data.find((item) => item.table === 'sheet1');
        const sheet1DTData = saphData ? saphData.fields : [];
        sheet1DTData.forEach((value, key) => (sheet1SetFromDecisionTree[key] = value));
      }
      const where = {};
      where['htz'] = { _eq: npiHtz };
      const sheet1Update = { ...record.sheet1, ...sheet1SetFromDecisionTree };
      if (record.rowData.qsfaneed !== undefined) {
        sheet1Update.qsfaneed = record.rowData.qsfaneed;
      }
      sheet1Update.speedline = getSpeedlineValue(
        record.rowData.speedline_dec,
        record.rowData.speedshop_status_id,
      );
      sheet1Update.indus_id_lastmodified = new Date().toISOString().substring(0, 19);
      sheet1Update.pre_htz_manufactured_by = record.rowData.pre_htz_manufactured_by;
      sheet1Update['updated_by'] = userId;
      sheet1Update['update_date'] = new Date().toISOString().substring(0, 19);
      const sheet1Set = removeNullUndefined({ ...sheet1Update });
      sheet1UpdateObjects.push({
        where,
        _set: sheet1Set,
      });
    }
  });

  return sheet1UpdateObjects;
}

export function calcFinRepoCommit({ upsertedRecords, userId }) {
  return upsertedRecords
    .filter((record) => !!record.rowData.npi_id && !!record.tblfinancerepository.nrp_o_category)
    .map((record) => {
      const finRepoSet = {
        ...record.tblfinancerepository,
        updated_by: userId,
        update_date: new Date().toISOString().substring(0, 19),
      };
      const where = {};
      where['npi_id'] = { _eq: record.rowData.npi_id };
      return { where, _set: finRepoSet };
    });
}

export function calcMutationPayload({
  npiDeleteIds,
  npiInsertObjects,
  npiUpdateObjects,
  saphInsertObjects,
  saphUpdateObjects,
  sheet1UpdateObjects,
  finRepoUpdateObjects,
  schemaName,
}) {
  const npiPayload = {
    deleteIds: npiDeleteIds,
    insertObjects: npiInsertObjects,
    updateObjects: npiUpdateObjects,
    pkey: 'npi_id',
    schema: schemaName,
    table: 'npi',
  };
  const saphPayload = {
    insertObjects: saphInsertObjects,
    updateObjects: saphUpdateObjects,
    pkey: 'id',
    schema: schemaName,
    table: 'e_saph',
  };
  const sheet1Payload = {
    updateObjects: sheet1UpdateObjects,
    pkey: 'id1',
    schema: schemaName,
    table: 'sheet1',
  };
  const tables = [];
  tables.push(npiPayload);
  if (saphInsertObjects.length || saphUpdateObjects.length) {
    tables.push(saphPayload);
  }
  if (sheet1UpdateObjects.length) {
    tables.push(sheet1Payload);
  }

  const finRepoTable = finRepoUpdateObjects.length
    ? {
        updateObjects: finRepoUpdateObjects,
        pkey: 'id',
        schema: schemaName,
        table: 'tblfinancerepository',
      }
    : null;

  return { tables, finRepoTable };
}
